"use client";

import React from "react";
import { Switch as AntSwitch, ConfigProvider } from "antd";
import type { SwitchProps as AntSwitchProps } from "antd";
import { orionTheme } from "../theme/orion-theme";
import clsx from "clsx";
import "../styles/switch.css";

export interface SwitchProps extends AntSwitchProps {
  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;
}

/**
 * Switch component - Ant Design Switch with OpenStore Orion theming
 *
 * This component provides FULL access to all Ant Design Switch props while
 * automatically applying the OpenStore Orion theme and Figma design specifications.
 *
 * Features:
 * - Figma design specifications (28x16px container, 12x12px handle, specific colors)
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design Switch features and props
 *
 * @example
 * ```tsx
 * // Basic switch with Orion theming
 * <Switch />
 *
 * // All Ant Design Switch props work
 * <Switch
 *   checked={isOn}
 *   onChange={handleChange}
 *   disabled={isDisabled}
 *   loading={isLoading}
 *   size="small"
 *   className="custom-switch"
 * />
 *
 * // With custom labels
 * <Switch
 *   checkedChildren="ON"
 *   unCheckedChildren="OFF"
 *   checked={isEnabled}
 *   onChange={setIsEnabled}
 * />
 * ```
 */
export const Switch: React.FC<SwitchProps> = ({ className, ...props }) => {
  return (
    <ConfigProvider theme={orionTheme}>
      <AntSwitch className={clsx("orion-switch", className)} {...props} />
    </ConfigProvider>
  );
};

Switch.displayName = "Switch";
